import React, { createContext, useState } from 'react';
import filter from 'lodash/filter';
import produce from 'immer';
import set from 'lodash/set';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { ChatCampaign, ChatCampaignStatus } from 'src/models/ChatCampaigns/ChatCampaign';
import ChatCampaignEditor from './ChatCampaignEditor';
import chatCampaignsResource from 'src/actions/chatCampaigns/chatCampaignsResource';
import ChatCampaignsSettingModal from './ChatCampaignsSettingModal';
import { columnWithTooltip } from 'src/presentation/components/pages/shared/Table';
import { Delete } from 'src/presentation/components/pages/shared/rowActions/Delete';
import { Edit } from 'src/presentation/components/pages/shared/rowActions/Edit';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import { OutlineButton } from 'src/presentation/components/agent_desktop_components';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import Toggle from 'src/presentation/components/forms/Toggle';
import useChatCampaignsWithAgents from './hooks/useChatCampaignsWithAgents';
import EmptyState from 'src/presentation/components/pages/proactiveCommunications/components/shared/EmptyState';

export interface ChatCampaignContextValue {
  chatCampaignsList: ChatCampaign[];
}

export const ChatCampaignContext = createContext<ChatCampaignContextValue>({
  chatCampaignsList: [],
});

export const helpDocsRender = (
  <a target="_blank" rel="noopener noreferrer" href={'https://help.gladly.com/docs/create-proactive-chat-campaign'}>
    Help Docs
  </a>
);

const defaultColumnStyle = { padding: '10px 16px' };

const columnConfig = ({ onToggleChange }) => [
  {
    attr: 'status',
    label: 'Status',
    customRenderer: (item: ChatCampaign) => (
      <Toggle
        data-aid={`toggle-${item.status === ChatCampaignStatus.ENABLED ? 'enabled' : 'disabled'}`}
        value={item.status === ChatCampaignStatus.ENABLED}
        onChange={() => onToggleChange(item)}
        debounceInterval={500}
      />
    ),
    customStyle: () => defaultColumnStyle,
  },
  {
    attr: 'priority',
    label: 'Order',
    customColumnRender: columnWithTooltip({
      dataAid: 'column-order-tooltip',
      message: (
        <>
          Proactive Chat goes through campaigns in this priority order,
          <br />
          until a campaign matches the criteria set. Then, Proactive Chat will display.
        </>
      ),
    }),
    customStyle: () => defaultColumnStyle,
  },
  {
    attr: 'name',
    label: 'Campaign Name',
    customStyle: () => defaultColumnStyle,
  },
  {
    attr: 'description',
    label: 'Description',
    customStyle: () => defaultColumnStyle,
  },
  {
    attr: 'updatedByString',
    label: 'Last Updated By',
    customStyle: () => defaultColumnStyle,
  },
  {
    attr: 'updatedAtString',
    label: 'Last Updated On',
    customStyle: () => defaultColumnStyle,
  },
];

const renderRowActions = ({ item }) => (
  <>
    <Delete
      confirmationText={`Delete ${item.name}`}
      deleteAction={chatCampaignsResource.effects.remove}
      description={
        <>
          Are you sure you want to delete? Deleting an active campaign
          <br /> means the campaign will be removed from
          <br />
          your site immediately and cannot be recovered.
        </>
      }
      id={item.id}
      submitText="Delete Campaign"
    />
    <Edit id={item.id} />
  </>
);

const ProactiveChatConfigurations: TablePageRender = ({ root }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { errors, loadingState, chatCampaigns } = useChatCampaignsWithAgents();
  const { updateChatCampaignStatus } = chatCampaignsResource.effects;
  const dispatch = useDispatch();

  const onToggleChange = item => {
    const changedItem: any = produce(item, draft => {
      set(
        draft,
        'status',
        draft.status === ChatCampaignStatus.ENABLED ? ChatCampaignStatus.DISABLED : ChatCampaignStatus.ENABLED
      );
    });
    dispatch(
      updateChatCampaignStatus(
        { id: changedItem.id },
        { ...changedItem, version: changedItem.version + 1 },
        {
          doNotShowSuccessToast: true,
          disableWindowBack: true,
        }
      )
    );
  };

  const openSettingModal = () => setIsModalOpen(true);
  const closeSettingModal = () => setIsModalOpen(false);

  const createButton = (
    <RouterLinkButton data-aid="addButton" to={`${root}/new`}>
      Create Campaign
    </RouterLinkButton>
  );
  const actions = (
    <>
      <MarginedOutlineButton data-aid="settingsButton" onClick={openSettingModal}>
        Settings
      </MarginedOutlineButton>
      {createButton}
    </>
  );

  const Description = (
    <span>
      Proactively reach out to your customers through automated chat messages. Visit our {helpDocsRender} to learn about
      chat Campaigns.
    </span>
  );

  function filterFunc(items: ChatCampaign[], searchString) {
    if (!searchString) {
      return items;
    }
    return filter(items, item => {
      const search = searchString.toLowerCase();
      return item.name.toLowerCase().includes(search) || item.description.toLowerCase().includes(search);
    });
  }

  const emptyState = <EmptyState title="Create your first Proactive Chat Campaign" actionsRender={createButton} />;

  const emptyStateRender = chatCampaigns.length ? null : emptyState;
  const contextValue = { chatCampaignsList: chatCampaigns };

  return (
    <>
      <ChatCampaignContext.Provider value={contextValue}>
        <TablePage
          actions={actions}
          columnConfig={columnConfig({ onToggleChange })}
          data-aid="admin-chat-campaigns-page"
          description={Description}
          editor={ChatCampaignEditor}
          emptyStateRender={emptyStateRender}
          errors={errors}
          filterFunc={filterFunc}
          isLoading={isLoadingStatus(loadingState)}
          items={chatCampaigns}
          name="Proactive Chat"
          renderRowActions={renderRowActions}
          resetErrors={chatCampaignsResource.actions.resetErrors}
          root={root}
        />
      </ChatCampaignContext.Provider>
      {isModalOpen && <ChatCampaignsSettingModal onClose={closeSettingModal} />}
    </>
  );
};

const MarginedOutlineButton = styled(OutlineButton)`
  margin-right: ${p => p.theme.spacing.large};
`;

export default ProactiveChatConfigurations;
